// src/utils/cropImage.js

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

/**
 * getCroppedImg
 * - Crops an image to the given pixel rectangle.
 * - When options.roundMask === true, applies a circular alpha mask so the result
 *   is a perfect circle with transparent corners.
 * - Always exports at 500×500 pixels (no DPR scaling).
 * - Returns a PNG (default) Blob to preserve transparency.
 *
 * @param {string} imageSrc dataURL or URL
 * @param {Object} pixelCrop { x, y, width, height } — from react-easy-crop
 * @param {Object} options { roundMask?: boolean, mimeType?: string, quality?: number }
 * @returns {Promise<Blob>} A blob containing the cropped image
 */
export const getCroppedImg = async (
  imageSrc,
  pixelCrop,
  options = { roundMask: false, mimeType: "image/png", quality: 0.92 }
) => {
  const { roundMask = false, mimeType = "image/png", quality = 0.92 } = options;

  const image = await createImage(imageSrc);

  // Final export dimensions (in pixels)
  const EXPORT_SIZE = 500;

  // Ensure crop values are safe
  const cropW = Math.max(1, Math.floor(pixelCrop.width));
  const cropH = Math.max(1, Math.floor(pixelCrop.height));

  // For round mask, make the source crop square
  const sourceSide = roundMask ? Math.min(cropW, cropH) : cropW;

  // Create the canvas at final size (no devicePixelRatio scaling)
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = EXPORT_SIZE;
  canvas.height = EXPORT_SIZE;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Calculate source crop start points
  const sx =
    pixelCrop.x + (roundMask && cropW > cropH ? (cropW - sourceSide) / 2 : 0);
  const sy =
    pixelCrop.y + (roundMask && cropH > cropW ? (cropH - sourceSide) / 2 : 0);
  const sWidth = roundMask ? sourceSide : cropW;
  const sHeight = roundMask ? sourceSide : cropH;

  // Draw scaled to exactly 500×500
  ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, EXPORT_SIZE, EXPORT_SIZE);

  if (roundMask) {
    // Apply circular alpha mask
    ctx.globalCompositeOperation = "destination-in";
    ctx.beginPath();
    const radius = EXPORT_SIZE / 2;
    ctx.arc(radius, radius, radius, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        try {
          Object.defineProperty(blob, "name", { value: "avatar.png" });
        } catch {
          // ignore if setting name fails
        }
        resolve(blob);
      },
      mimeType, // "image/png" by default to preserve transparency
      quality
    );
  });
};
